import { GregorianCalendar, ZonedDateTime } from '@internationalized/date';
import {
  AriaDatePickerProps,
  AriaTimeFieldProps,
  useDateField,
  useDateSegment,
  useTimeField,
} from '@react-aria/datepicker';
import {
  DateFieldState,
  useDateFieldState,
  DateSegment,
  useTimeFieldState,
  DatePickerState,
} from '@react-stately/datepicker';
import { useRef } from 'react';

import { fontStyles } from '@/styles/typography';

import { CalendarIcon } from '@/ui/Icons/CalendarIcon';

/**
 * The createCalendar function from the `@internationalized/date` package includes all calendar types.
 * We can use this function to return the Gregorian Calendar until internationalization is enabled.
 */
function createCalendar() {
  return new GregorianCalendar();
}

interface CalendarDateSegmentProps {
  segment: DateSegment;
  state: DateFieldState;
}
/**
 * Calendar Date Time Field input segment.
 */
const CalendarDateSegment = ({ segment, state }: CalendarDateSegmentProps) => {
  const ref = useRef(null);
  const { segmentProps } = useDateSegment(segment, state, ref);
  return (
    <span
      {...segmentProps}
      ref={ref}
      aria-labelledby={segment.text}
      className="mr-0.5 focus:bg-token-alphalight-400 focus:rounded-[.125rem] focus:outline-none"
    >
      {segment.text}
    </span>
  );
};

interface CalendarDateFieldProps {
  state: DatePickerState;
  datePickerProps: AriaDatePickerProps<ZonedDateTime>;
  timeFieldProps: AriaTimeFieldProps<ZonedDateTime>;
  onClick: () => void;
}
const CalendarDateTimeField = ({
  state,
  onClick,
  datePickerProps,
  timeFieldProps,
}: CalendarDateFieldProps) => {
  // date field
  const dateInputState = useDateFieldState<ZonedDateTime>({
    ...datePickerProps,
    locale: 'en-US',
    createCalendar,
  });
  const dateFieldRef = useRef(null);
  const { fieldProps, labelProps } = useDateField(datePickerProps, dateInputState, dateFieldRef);
  const dateSegments = dateInputState.segments.slice(0, 5);

  // time field
  const timeFieldRef = useRef(null);
  const timeInputState = useTimeFieldState({
    ...timeFieldProps,
    locale: 'en-US',
    hideTimeZone: true,
  });
  const { labelProps: timeFieldLabelProps, fieldProps: timeFieldInputProps } = useTimeField(
    timeFieldProps,
    timeInputState,
    timeFieldRef,
  );
  const timeInputSegments = timeInputState.segments;

  return (
    <div className="pt-5.5 pb-2 block tablet:grid gap-x-5.5 grid-cols-[1fr_1fr]">
      <div>
        <span
          {...labelProps}
          className={fontStyles({ variant: 'LABEL_MD', class: 'mb-2 flex text-350' })}
        >
          Date
        </span>
        <div
          data-state={dateInputState.isInvalid ? 'invalid' : state.isOpen ? 'open' : undefined}
          className={fontStyles({
            variant: 'BODY_MD',
            class: [
              'bg-token-alphalight-200',
              'rounded-sm',
              'h-9',
              'border-none',
              'hover:outline-1',
              'hover:outline-token-gray-500',
              'focus:bg-token-color-gray-200',
              'focus:outline-2',
              'focus:outline-token-purple-1000',
              'px-3',
              'flex',
              'justify-center',
              'items-center',
              'data-[state=invalid]:outline-token-red-800',
              'data-[state=open]:outline-token-purple-900',
              'focus:outline-token-purple-900',
              'active:outline-token-purple-900',
              'has-[span:focus]:outline-token-purple-900',
              'data-[state=invalid]:outline-2',
              'data-[state=open]:outline-2',
              'focus:outline-2',
              'active:outline-2',
              'has-[span:focus]:outline-2',
            ],
          })}
          role="button"
          onClick={onClick}
        >
          <div className="flex justify-between h-full w-full items-center">
            <span {...fieldProps} ref={dateFieldRef} className="flex justify-start">
              {dateSegments.map((segment, i) => (
                <CalendarDateSegment key={i} segment={segment} state={dateInputState} />
              ))}
            </span>
            <span
              className={fontStyles({
                variant: 'LABEL_LG',
                class: [
                  'bg-transparent',
                  'border-0',
                  'text-inherit',
                  'leading-[inherit]',
                  'p-0',
                  'h-4',
                  'w-4',
                  'flex',
                  'focus:outline-2',
                  'focus:outline-token-purple-900',
                ],
              })}
            >
              <CalendarIcon />
            </span>
          </div>
        </div>
      </div>
      <div>
        <span
          {...timeFieldLabelProps}
          className={fontStyles({ variant: 'LABEL_MD', class: 'mb-2 flex text-350' })}
        >
          Time
        </span>
        <div>
          <div
            {...timeFieldInputProps}
            data-state={dateInputState.isInvalid ? 'invalid' : undefined}
            className={fontStyles({
              variant: 'BODY_MD',
              class: [
                'bg-token-alphalight-200',
                'rounded-sm',
                'h-9',
                'border-none',
                'hover:outline-1',
                'hover:outline-token-gray-500',
                'focus:bg-token-color-gray-200',
                'focus:outline-2',
                'focus:outline-token-purple-1000',
                'px-3',
                'flex',
                'justify-center',
                'items-center',
                'data-[state=invalid]:outline-token-red-800',
                'data-[state=open]:outline-token-purple-900',
                'focus:outline-token-purple-900',
                'active:outline-token-purple-900',
                'has-[span:focus]:outline-token-purple-900',
                'data-[state=invalid]:outline-2',
                'data-[state=open]:outline-2',
                'focus:outline-2',
                'active:outline-2',
                'has-[span:focus]:outline-2',
              ],
            })}
            ref={timeFieldRef}
          >
            <span className="flex h-full w-full items-center" aria-label="Time">
              {timeInputSegments.map((segment, i) => (
                <CalendarDateSegment key={i} segment={segment} state={timeInputState} />
              ))}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export { CalendarDateTimeField };
